import {streetCarriesCity} from "../../misc/location-helpers";

/*
 * Text helpers for the list cards, ported from the Android list pages so the
 * web cards read exactly like the phone's.
 */

/** Trimmed, with the geocoder's "UNKNOWN" placeholder read as blank. */
const addressPart = (value?: string | null): string => {
  const trimmed = value?.trim() ?? '';
  return trimmed.toUpperCase() === 'UNKNOWN' ? '' : trimmed;
};

/**
 * An address on one line, as Android's `addressOneLine` writes it on a card:
 * the street, then whichever of city and state it does not already carry. No
 * zip: the cards never show one.
 */
export const addressOneLine = (street?: string | null, city?: string | null, state?: string | null): string => {
  const s = addressPart(street);
  if (streetCarriesCity(s, addressPart(city))) return s;
  return [s, addressPart(city), addressPart(state)].filter(Boolean).join(', ');
};

/** "EstimatePendingApproval" → "Pending Approval", "Cancelled" → "Canceled" (Android's prettyStatusName). */
export const prettyStatusName = (name: string): string => {
  const spaced = name.replace(/(?<=[a-z0-9])(?=[A-Z])/g, ' ');
  const withoutPrefix = spaced.startsWith('Estimate ') ? spaced.slice('Estimate '.length) : spaced;
  return (withoutPrefix.trim() ? withoutPrefix : spaced).replace('Cancelled', 'Canceled');
};

const currency = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'});

/** "$4,850" — whole-dollar totals drop the cents, like Android's formatMoney. */
export const formatMoney = (value: number): string => currency.format(value).replace(/\.00$/, '');

/** "$68.00/hr" — a rate always shows its cents (Android's formatHourlyRate). */
export const formatHourlyRate = (value: number): string => `${currency.format(value)}/hr`;

/**
 * A stored phone number for a card. Seeded and imported rows can be bare
 * digits, so a plain 10-digit number gets the "(208) 880-1195" mask; anything
 * else (an extension, a country code) is shown as it is.
 */
export const displayPhone = (raw?: string | null): string | undefined => {
  const phone = raw?.trim();
  if (!phone) return undefined;
  const digits = phone.replace(/\D/g, '');
  if (digits.length !== 10) return phone;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ordinalSuffix = (day: number): string => {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

const clockTime = (hours: number, minutes: number): string =>
  `${hours % 12 === 0 ? 12 : hours % 12}:${String(minutes).padStart(2, '0')} ${hours < 12 ? 'AM' : 'PM'}`;

/**
 * A job's schedule as one card line, from the "YYYY-MM-DD" date and "HH:MM"
 * start time the server stores: "Thu, Aug 27th · 7:30 AM". Only what is saved;
 * undefined when nothing is. Android also adds the estimated finish when a
 * crew is picked; the web has no job-time calculation to derive it from yet.
 */
export const scheduleListLine = (scheduledDate?: string | null, scheduledStartTime?: string | null): string | undefined => {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(scheduledDate ?? '');
  const date = dateMatch
    ? (() => {
      const d = new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]));
      return `${WEEKDAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}${ordinalSuffix(d.getDate())}`;
    })()
    : undefined;
  const timeMatch = /^(\d{1,2}):(\d{2})/.exec(scheduledStartTime ?? '');
  const time = timeMatch ? clockTime(Number(timeMatch[1]), Number(timeMatch[2])) : undefined;
  const parts = [date, time].filter(Boolean);
  return parts.length ? parts.join(' · ') : undefined;
};

/**
 * "Mon, Aug 24 at 8:30 AM" — the appointment card's date format; a date booked
 * without a time of day reads "Mon, Aug 24".
 */
export const formatApptTime = (value?: Date | string | null): string | undefined => {
  if (!value) return undefined;
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const date = `${WEEKDAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}`;
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
  return hasTime ? `${date} at ${clockTime(d.getHours(), d.getMinutes())}` : date;
};
